import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon } from 'lucide-react';
import { PreviewLightbox } from './PreviewLightbox';
import { previewPages } from '../data/offer';
export function PreviewCarousel() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [expanded, setExpanded] = useState(false);
  const reduceMotion = useReducedMotion();
  const total = previewPages.length;
  const go = useCallback((step: number) => setState(([current]) => [(current + step + total) % total, step]), [total]);
  const close = useCallback(() => setExpanded(false), []);
  // Warm up neighbours so switching feels instant, without blocking first paint.
  useEffect(() => {
    [index + 1, index - 1].forEach(i => {
      const img = new Image();
      img.src = previewPages[(i + total) % total].src;
    });
  }, [index, total]);
  const offset = reduceMotion ? 0 : 40;
  return <section className="bg-surface px-4 py-16 sm:py-24" aria-roledescription="carrusel" aria-label="Páginas del kit" data-id="element-96" data-mp="35odif2b625an">
      <div className="mx-auto max-w-5xl" data-id="element-97" data-mp="3amf29pgf28n6">
        <header className="text-center" data-id="element-98" data-mp="3fli7b9wecev5">
          <h2 className="font-display text-3xl font-bold text-highlight sm:text-4xl" data-id="element-99" data-mp="3fkgmidpkwe08">
            👀 MIRÁ POR DENTRO DEL KIT
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-foreground-muted" data-id="element-100" data-mp="3odk5t2mwnl2s">
            Conocé algunas de las páginas del producto 👇
          </p>
        </header>
        <div className="mt-10 flex items-center justify-center gap-3 sm:gap-6" data-id="element-101" data-mp="3tb2501b4elxl">
          <ArrowButton label="Página anterior" onClick={() => go(-1)} data-id="element-102" data-mp="27dgkj7uyqvc">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-103" data-mp="7556x8suu4xf" />
          </ArrowButton>
          <div className="group relative aspect-[792/980] w-full max-w-md overflow-hidden rounded-2xl border border-highlight/20 bg-canvas shadow-2xl" data-id="element-104" data-mp="23xozw9v6otvf">
            <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-105" data-mp="28xbkx5h97ees">
              <motion.img key={index} src={previewPages[index].src} alt={previewPages[index].alt} width="792" height="980" loading="lazy" decoding="async" draggable={false} custom={direction} initial={{
              opacity: 0,
              x: direction * offset
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -offset
            }} transition={{
              duration: 0.24,
              ease: [0.23, 1, 0.32, 1]
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onTap={() => setExpanded(true)} onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);else if (info.offset.x > 60) go(-1);
            }} className="absolute inset-0 h-full w-full cursor-zoom-in touch-pan-y object-cover" data-id="element-106" data-mp="20x58xkws9wjq" />
            </AnimatePresence>
            <button type="button" onClick={() => setExpanded(true)} aria-label="Ver página en pantalla completa" className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-canvas/85 px-3 py-2 text-xs font-semibold text-foreground backdrop-blur-sm transition-colors duration-150 ease-out hover:bg-highlight hover:text-canvas" data-id="element-107" data-mp="2yl5p8oik4zay">
              <Maximize2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-108" data-mp="3d6b4koo18haa" />
              Ampliar
            </button>
          </div>
          <ArrowButton label="Página siguiente" onClick={() => go(1)} data-id="element-109" data-mp="2yglx6bpcjh1k">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-110" data-mp="3383rwi0s0le3" />
          </ArrowButton>
        </div>
        <div className="mt-6 flex items-center justify-center gap-2" data-id="element-111" data-mp="2phidp6m9ozmj">
          {previewPages.map((page, i) => <button key={page.src} type="button" onClick={() => setState([i, i > index ? 1 : -1])} aria-label={`Ver página ${i + 1}`} aria-current={i === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-7 bg-highlight' : 'w-2.5 bg-foreground/25 hover:bg-foreground/50'}`} data-id="element-112" data-mp="2thskglhvvu2g" data-mpi={i} />)}
        </div>
        <p className="mt-3 text-center text-sm text-foreground-subtle" aria-live="polite" data-id="element-113" data-mp="3mxef1wsqaent">
          {index + 1} / {total}
        </p>
      </div>
      <AnimatePresence data-id="element-114" data-mp="3e26teud5ign3">
        {expanded && <PreviewLightbox index={index} direction={direction} onNavigate={go} onClose={close} data-id="element-115" data-mp="3e15h6mkefo3s" />}
      </AnimatePresence>
    </section>;
}
interface ArrowButtonProps {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}
function ArrowButton({
  label,
  onClick,
  children
}: ArrowButtonProps) {
  return <button type="button" onClick={onClick} aria-label={label} className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-highlight/40 bg-canvas text-highlight transition-colors duration-150 ease-out hover:bg-highlight hover:text-canvas sm:h-14 sm:w-14" data-id="element-116" data-mp="2z7ttd656x3au">
      {children}
    </button>;
}